/**
 * PageThemeConfigContext - Single source of truth for page-level theming
 *
 * This is the ONLY theme context needed for page-level customization.
 * ThemeProvider reads from this to generate custom Chakra themes and set color mode.
 *
 * Architecture:
 * ```
 * _app.tsx
 *   └── PageThemeConfigProvider  ← This context
 *         └── ThemeProvider      ← Reads config, generates theme, sets color mode
 *               └── Page content
 * ```
 *
 * Usage:
 * ```tsx
 * // In any component (typically DetailPageLayoutContent):
 * useSetPageThemeConfig({
 *   colorMode: "dark",
 *   primaryColor: "#FF5500",
 * });
 * ```
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import type { LayoutThemeConfig } from "@/shared/config/layout.config";

interface PageThemeConfigContextValue {
  themeConfig: LayoutThemeConfig | undefined;
  setThemeConfig: (config: LayoutThemeConfig | undefined) => void;
}

const PageThemeConfigContext = createContext<
  PageThemeConfigContextValue | undefined
>(undefined);

/**
 * Provider - place in _app.tsx above ThemeProvider
 */
export function PageThemeConfigProvider({ children }: { children: ReactNode }) {
  const [themeConfig, setThemeConfigState] = useState<
    LayoutThemeConfig | undefined
  >(undefined);

  const setThemeConfig = useCallback(
    (config: LayoutThemeConfig | undefined) => {
      setThemeConfigState(config);
    },
    []
  );

  return (
    <PageThemeConfigContext.Provider value={{ themeConfig, setThemeConfig }}>
      {children}
    </PageThemeConfigContext.Provider>
  );
}

/**
 * Hook for ThemeProvider to read current config
 */
export function usePageThemeConfig(): LayoutThemeConfig | undefined {
  const context = useContext(PageThemeConfigContext);
  return context?.themeConfig;
}

/**
 * Hook for pages to set their theme config
 * Sets both custom colors AND color mode in one call
 */
export function useSetPageThemeConfig(
  config: LayoutThemeConfig | undefined
): void {
  const context = useContext(PageThemeConfigContext);

  useEffect(() => {
    if (context) {
      context.setThemeConfig(config);
    }
    return () => {
      context?.setThemeConfig(undefined);
    };
  }, [context, config]);
}
